import DayjsUtils from '@date-io/dayjs';
import {
  Button,
  Divider,
  Grid,
  Input,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  DatePicker,
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import dayjs from 'dayjs';
import ptBR from 'dayjs/locale/pt-br';
import { getIn, useFormik } from 'formik';
import { IRequestResponse } from 'interfaces/RequestResponse';
import React from 'react';
import { useQuery } from 'react-query';
import { getYesOrNotOption } from 'services/activity';
import {
  postFinishRealActivitiesPlanning,
  postFinishPlanActivitiesPlanning,
} from 'services/planning/grid/planningGrid';
import * as yup from 'yup';
import useStyles from './style';
import { IActivity, IOption, IPlanningModalFinishProps } from './types';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';

const FORM_ID = 'PLANNING_FORM_FINISH';

const MAX_DATE = dayjs().format('YYYY-MM-DD');

const PlanningModalFinish = ({
  children,
  open,
  onClose,
  selectedRows,
  reloadData,
}: IPlanningModalFinishProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [outageOptions, setOutageOptions] = React.useState<IOption[]>([]);
  const [utilizationOptions, setUtilizationOptions] = React.useState<IOption[]>(
    [],
  );
  const validationSchema = yup.object({
    activities: yup
      .array()
      .of(
        yup.object({
          activityPlanId: yup.number().nullable(),
          activityRealId: yup.number().nullable(),
          realStart: yup
            .mixed()
            .required(DEFAULT_OK_TEXT)
            .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
              const test = dayjs(value);
              const maxDate = new Date();
              return (
                dayjs(value).isValid() &&
                (test.isSame(maxDate, 'minute') ||
                  test.isBefore(maxDate, 'minute'))
              );
            }),
          realCompletion: yup
            .mixed()
            .required(DEFAULT_OK_TEXT)
            // eslint-disable-next-line func-names
            .test('is-greater-than', DEFAULT_ERROR_TEXT, function (value) {
              const test = dayjs(value);
              const maxDate = new Date();
              // eslint-disable-next-line react/no-this-in-sfc
              const { realStart } = this.parent;

              const validation =
                // não gera erro se o início previsto n for digitado
                !realStart ||
                // testa a data e verifica a validação de data mínima,
                // o segundo parâmetro utilizado é a granularidade da comparação
                (test.isValid() &&
                  (test.isSame(realStart, 'minute') ||
                    test.isAfter(realStart, 'minute')) &&
                  // testa a data e verifica a validação de data máxima,
                  // o segundo parâmetro utilizado é a granularidade da comparação
                  (test.isSame(maxDate, 'minute') ||
                    test.isBefore(maxDate, 'minute')));

              return validation;
            }),
          utilization: yup.string().required(DEFAULT_OK_TEXT),
          outage: yup.string().required(DEFAULT_OK_TEXT),
        }),
      )
      .min(selectedRows.length)
      .required(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      activities: selectedRows.map(row => {
        return {
          activityRealId: row?.activityRealId,
          activityPlanId: row?.activityPlanId,
          realStart: row?.realStart,
          utilization: row?.utilization,
          outage: row?.outage,
          realCompletion: null,
        };
      }) as unknown as IActivity[],
    },
    validationSchema,
    onSubmit: formValues => {
      const params = formValues.activities.map(value => {
        const {
          utilization,
          outage,
          realStart,
          realCompletion,
          activityRealId,
          activityPlanId,
        } = value;
        return {
          utilization,
          outage,
          realStart,
          realCompletion,
          activityRealId,
          activityPlanId,
        };
      });

      const realActivitiesParams = params.filter(item => item.activityRealId);
      const planActivitiesParams = params.filter(item => !item.activityRealId);
      const arrayOfRequest: Promise<IRequestResponse<null>>[] = [];

      if (realActivitiesParams.length)
        arrayOfRequest.push(
          postFinishRealActivitiesPlanning(realActivitiesParams),
        );

      if (planActivitiesParams.length)
        arrayOfRequest.push(
          postFinishPlanActivitiesPlanning(planActivitiesParams),
        );

      // se for enviado com sucesso
      setLoadingSaveForm(true);
      Promise.all(arrayOfRequest)
        .then(() => {
          // solicita um reload de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const updateDate = React.useCallback(
    (fieldName, newDate, format) => {
      const date = dayjs(newDate, format, true);

      if (date.isValid()) {
        handleDateChange(fieldName, date.toISOString());
      } else {
        handleDateChange(fieldName, newDate);
      }

      if (!newDate) {
        handleDateChange(fieldName, null);
      }
    },
    [handleDateChange],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const enableQueries = React.useMemo(() => open, [open]);

  const enableOutageOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableUtilizationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryOutageOptions = useQuery({
    queryKey: ['outageOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setOutageOptions(data);
    },
    enabled: enableOutageOptions,
  });

  const queryUtilizationOptions = useQuery({
    queryKey: ['utilizationOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setUtilizationOptions(data);
    },
    enabled: enableUtilizationOptions,
  });

  const renderBody = React.useCallback(() => {
    return (
      <div className={classes.formScroll}>
        <form id={FORM_ID} onSubmit={formik.handleSubmit}>
          <Grid>
            <Typography className={classes.formMessage}>
              Preencha os dados obrigatórios para concluir a(s) atividade(s):
            </Typography>
          </Grid>
          {selectedRows.map((row, index) => {
            return (
              <Grid container className={classes.bodyContent}>
                {index > 0 && (
                  <div>
                    <Divider className={classes.divider} />
                  </div>
                )}
                <Input
                  name={`activities.[${index}].activityRealId`}
                  value={row?.activityRealId}
                  inputProps={{ type: 'hidden' }}
                  onChange={formik.handleChange}
                />

                <Input
                  name={`activities.[${index}].activityPlanId`}
                  value={row?.activityPlanId}
                  inputProps={{ type: 'hidden' }}
                  onChange={formik.handleChange}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="activity"
                      label="Atividade"
                      variant="outlined"
                      value={row?.activity}
                      disabled
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      name="planStart"
                      label="Início previsto"
                      inputVariant="outlined"
                      value={row?.startPrev}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      name="planCompletion"
                      label="Término previsto"
                      inputVariant="outlined"
                      value={row?.endPrev}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      name="startReplan"
                      label="Início replanejado"
                      inputVariant="outlined"
                      value={row?.startReplan}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      name="replanCompletion"
                      label="Término replanejado"
                      inputVariant="outlined"
                      value={row?.endReplan}
                      disabled
                      onChange={formik.handleChange}
                      format="DD/MM"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="equipment"
                      label="Equipamento"
                      variant="outlined"
                      value={row?.equipment}
                      disabled
                    />
                  </Grid>
                </Grid>
                {/* START ROW 3 */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      name={`activities.${index}.outage`}
                      label="Indisp real"
                      select
                      variant="outlined"
                      value={
                        formik.values.activities[index]?.outage ||
                        row?.outage ||
                        null
                      }
                      onChange={formik.handleChange}
                      error={
                        getIn(formik.touched, `activities.[${index}].outage`) &&
                        Boolean(
                          getIn(formik.errors, `activities.[${index}].outage`),
                        )
                      }
                      helperText={
                        (getIn(
                          formik.touched,
                          `activities.[${index}].outage`,
                        ) &&
                          getIn(
                            formik.errors,
                            `activities.[${index}].outage`,
                          )) ||
                        DEFAULT_HELPER_TEXT
                      }
                      inputProps={{
                        endAdornment: (
                          <LoadingInputEndAdornment
                            loading={queryOutageOptions?.isLoading}
                          />
                        ),
                      }}
                    >
                      {outageOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name={`activities.${index}.utilization`}
                      label="Aproveitamento real"
                      select
                      variant="outlined"
                      value={
                        formik.values.activities[index]?.utilization ||
                        row?.utilization ||
                        null
                      }
                      onChange={formik.handleChange}
                      error={
                        getIn(
                          formik.touched,
                          `activities.[${index}].utilization`,
                        ) &&
                        Boolean(
                          getIn(
                            formik.errors,
                            `activities.[${index}].utilization`,
                          ),
                        )
                      }
                      helperText={
                        (getIn(
                          formik.touched,
                          `activities.[${index}].utilization`,
                        ) &&
                          getIn(
                            formik.errors,
                            `activities.[${index}].utilization`,
                          )) ||
                        DEFAULT_HELPER_TEXT
                      }
                      inputProps={{
                        endAdornment: (
                          <LoadingInputEndAdornment
                            loading={queryUtilizationOptions?.isLoading}
                          />
                        ),
                      }}
                    >
                      {utilizationOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                {/* END ROW 3 */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <KeyboardDateTimePicker
                      name={`activities.${index}.realStart`}
                      label="Início real"
                      inputVariant="outlined"
                      format="DD/MM/YY - HH:mm"
                      cancelLabel="Cancelar"
                      clearLabel="Limpar"
                      clearable
                      ampm={false}
                      maxDate={MAX_DATE}
                      onChange={(date, value) => {
                        updateDate(
                          `activities.${index}.realStart`,
                          value,
                          'DD/MM/YY - HH:mm',
                        );
                      }}
                      value={
                        formik.values.activities[index]?.realStart ||
                        row?.realStart ||
                        null
                      }
                      helperText={
                        (getIn(
                          formik.touched,
                          `activities.[${index}].realStart`,
                        ) &&
                          getIn(
                            formik.errors,
                            `activities.[${index}].realStart`,
                          )) ||
                        DEFAULT_HELPER_TEXT
                      }
                      error={
                        getIn(
                          formik.touched,
                          `activities.[${index}].realStart`,
                        ) &&
                        Boolean(
                          getIn(
                            formik.errors,
                            `activities.[${index}].realStart`,
                          ),
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <KeyboardDateTimePicker
                      name={`activities.${index}.realCompletion`}
                      label="Término real"
                      inputVariant="outlined"
                      format="DD/MM/YY - HH:mm"
                      cancelLabel="Cancelar"
                      clearLabel="Limpar"
                      clearable
                      ampm={false}
                      minDate={
                        formik.values?.activities[index]?.realStart || undefined
                      }
                      maxDate={MAX_DATE}
                      onChange={(date, value) => {
                        updateDate(
                          `activities.${index}.realCompletion`,
                          value,
                          'DD/MM/YY - HH:mm',
                        );
                      }}
                      value={
                        formik.values.activities[index]?.realCompletion || null
                      }
                      helperText={
                        (getIn(
                          formik.touched,
                          `activities.[${index}].realCompletion`,
                        ) &&
                          getIn(
                            formik.errors,
                            `activities.[${index}].realCompletion`,
                          )) ||
                        DEFAULT_HELPER_TEXT
                      }
                      error={
                        getIn(
                          formik.touched,
                          `activities.[${index}].realCompletion`,
                        ) &&
                        Boolean(
                          getIn(
                            formik.errors,
                            `activities.[${index}].realCompletion`,
                          ),
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </form>
      </div>
    );
  }, [
    classes.formScroll,
    classes.formMessage,
    classes.bodyContent,
    classes.divider,
    formik.handleSubmit,
    formik.handleChange,
    formik.values.activities,
    formik.touched,
    formik.errors,
    selectedRows,
    queryOutageOptions?.isLoading,
    outageOptions,
    queryUtilizationOptions?.isLoading,
    utilizationOptions,
    updateDate,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Concluir"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="628px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};
export default PlanningModalFinish;
